import type {
  IntegrationConnectionMode,
  IntegrationValidationStatus,
} from "@playbookai/api-clients";

/**
 * How to say a connection mode to an administrator.
 *
 * The select used to render the wire values themselves — an admin choosing how
 * their workspace connects to another system was reading `delegated_user`.
 * That is an internal key, not a sentence, and it tells someone who has not
 * read the schema nothing about what they are choosing.
 *
 * SAFE TO MAP, unlike anything provider-shaped in this folder. Connection mode
 * is a CLOSED vocabulary in the shared contract — three values that belong to
 * the platform, not to any connector — so naming them here adds no provider
 * knowledge to the shell. The rule this folder lives under is that
 * provider-specific strings come from the manifest; this is not one.
 *
 * Typed as an exhaustive `Record`, so adding a fourth mode to the contract
 * breaks the build here rather than rendering a blank option. That is the
 * opposite of the choice made for `IntegrationErrorCode` on the health surface,
 * and deliberately: error codes are an open, growing set where an honest
 * generic line beats a guessed paraphrase, while these three are a fixed choice
 * an administrator has to understand before they can pick one.
 */
export const CONNECTION_MODE_LABELS = {
  delegated_user: "Each person connects their own account",
  tenant_shared: "One shared account for the whole workspace",
  mixed: "Either — a shared account, or people connect their own",
} as const satisfies Record<IntegrationConnectionMode, string>;

/**
 * What choosing that mode actually means, in terms of what the OTHER system
 * ends up seeing. Shown as the field's hint, because the consequence — whose
 * name appears against the work — is the part an administrator is really
 * deciding, and it is not recoverable from the label alone.
 */
export const CONNECTION_MODE_HINTS = {
  delegated_user:
    "Work is done under each person's own account, so the other system shows who did it and applies their permissions there.",
  tenant_shared:
    "Work is done under a single workspace account, so the other system shows that account rather than the person who asked for it.",
  mixed:
    "People can connect their own account, and the shared workspace account covers anyone who has not.",
} as const satisfies Record<IntegrationConnectionMode, string>;

/** The label for a mode, for use in the connection-mode picker. */
export function connectionModeLabel(mode: IntegrationConnectionMode): string {
  return CONNECTION_MODE_LABELS[mode];
}

/**
 * The hint for the currently selected mode, or a prompt to choose one.
 *
 * Takes the empty string too, because the form starts with nothing selected
 * and the field should still explain what it is asking for.
 */
export function connectionModeHint(
  mode: IntegrationConnectionMode | "",
): string {
  return mode === ""
    ? "Choose whose account this workspace uses when it talks to the other system."
    : CONNECTION_MODE_HINTS[mode];
}

/**
 * A validation check's outcome, in the console's own voice.
 *
 * Rendered straight from the wire before, so the badge read a lower-case
 * `pass` / `warn` / `fail` / `skipped` while every other status word on the
 * page is sentence case. Milder than the connection-mode leak — these are at
 * least English — but the same habit: a wire value shown because it happened
 * to be readable.
 *
 * Exhaustive for the same reason as the modes above: a fifth status added to
 * the contract should break the build, not render lower-case next to four
 * capitalised siblings.
 */
export const VALIDATION_STATUS_LABELS = {
  pass: "Passed",
  warn: "Warning",
  fail: "Failed",
  skipped: "Skipped",
} as const satisfies Record<IntegrationValidationStatus, string>;

/** The label for a check's outcome, for use in its badge. */
export function validationStatusLabel(
  status: IntegrationValidationStatus,
): string {
  return VALIDATION_STATUS_LABELS[status];
}
